import React, { useState, useEffect, useCallback } from 'react';

import { Box, IconButton, Tooltip, Typography } from '@mui/material';

import VisibilityIcon from '@mui/icons-material/Visibility';

import DownloadIcon from '@mui/icons-material/Download';

import AddIcon from '@mui/icons-material/Add';

import { useHistory, useRouteMatch } from 'react-router-dom';

import PageHeader from '../../components/PageHeader';

import InvDataGrid from '../../components/InvDataGrid';

import StatusChip from '../../components/StatusChip';

import InvButton from '../../components/InvButton';

import FilterToolbar from '../../components/FilterToolbar';

import { getPurchaseInvoices } from '../../api/purchaseInvoice';

import useVendorLookup from '../../hooks/useVendorLookup';

import usePoGrnLookup from '../../hooks/usePoGrnLookup';

import { formatDate, formatCurrency, exportToCsv } from '../../utils';

import { INVOICE_STATUS_OPTIONS } from '../../constants';

import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';




function normalizeList(data) {

  return Array.isArray(data) ? data : (data?.items || data?.data || []);

}



export default function PurchaseInvoiceList() {

  const { can } = useInvRole();

  const { url } = useRouteMatch();

  const history = useHistory();

  const base = url.replace('/purchase-invoices', '');

  const { resolveVendorName } = useVendorLookup();

  const { resolvePoNumber, resolveGrnNumber } = usePoGrnLookup();

  const [invoices, setInvoices] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');

  const [status, setStatus] = useState('');



  const load = useCallback(async () => {

    setLoading(true);

    setError(null);

    try {

      const filters = {};

      if (status) filters.status = status;

      // API: GET /inventory/purchase-invoices/

      const data = await getPurchaseInvoices({}, filters);

      setInvoices(normalizeList(data));

    } catch (e) {

      setError(e.message);

    } finally {

      setLoading(false);

    }

  }, [status]);



  useEffect(() => { load(); }, [load]);



  const filteredInvoices = search

    ? invoices.filter(inv => (inv.invoice_number || '').toLowerCase().includes(search.toLowerCase()))

    : invoices;



  const columns = [

    { field: 'invoice_number', headerName: 'Invoice #', width: 170 },

    { field: 'vendor_id', headerName: 'Vendor', width: 190,

      valueGetter: p => resolveVendorName(p.row.vendor_id) },

    { field: 'purchase_order_id', headerName: 'PO #', width: 130,

      valueGetter: p => resolvePoNumber(p.row.purchase_order_id, p.row.po_number) },

    { field: 'grn_id', headerName: 'GRN #', width: 130,

      valueGetter: p => resolveGrnNumber(p.row.grn_id, p.row.grn_number) },

    { field: 'invoice_date', headerName: 'Invoice Date', width: 120, valueFormatter: p => formatDate(p.value) },

    { field: 'due_date', headerName: 'Due Date', width: 120, valueFormatter: p => formatDate(p.value) },

    { field: 'total_amount', headerName: 'Amount', width: 140, align: 'right', headerAlign: 'right',

      valueFormatter: p => formatCurrency(p.value) },

    { field: 'status', headerName: 'Status', width: 120,

      renderCell: p => <StatusChip status={p.value} /> },

    { field: 'posted_to_finance', headerName: 'Finance', width: 110, sortable: false,

      renderCell: p => (p.value

        ? <StatusChip status="POSTED" />

        : <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 12 }}>Pending</Typography>) },

    { field: 'actions', headerName: '', width: 70, sortable: false,

      renderCell: (p) => {

        const invoiceId = p.row.purchase_invoice_id || p.row.id;

        return (

          <Tooltip title="View">

            <IconButton size="small" onClick={() => history.push(`${base}/purchase-invoice/${invoiceId}`)}

              sx={{ color: IPD_COLORS.primary }}>

              <VisibilityIcon fontSize="small" />

            </IconButton>

          </Tooltip>

        );

      } },

  ];



  return (

    <Box>

      <PageHeader eyebrow="Material Management" title="Purchase Invoices"

        description={`${invoices.length} invoices`}

        actions={

          <>

            <InvButton startIcon={<DownloadIcon />} variant="outlined" tone="teal" size="small"

              onClick={() => exportToCsv('purchase-invoices.csv', filteredInvoices)}>Export</InvButton>

            {can(INV_ACTIONS.PURCHASE_INVOICE_CREATE) && (

              <InvButton startIcon={<AddIcon />} size="small"

                onClick={() => history.push(`${base}/purchase-invoice/new`)}>New Invoice</InvButton>

            )}

          </>

        }

      />






      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="Search by invoice #..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <FilterToolbar.Filter
          label="Status"
          width="wide"
          value={status}
          onChange={e => setStatus(e.target.value)}
          options={INVOICE_STATUS_OPTIONS}
        />
      </FilterToolbar>



      <InvDataGrid fillHeight rows={filteredInvoices} columns={columns} loading={loading} error={error} onRetry={load}

        getRowId={r => r.purchase_invoice_id || r.id}

        emptyMessage="No purchase invoices yet."

        emptyDescription="Create an invoice against a posted GRN to start the 3-way match and finance payment flow."

        emptyAction={

          can(INV_ACTIONS.PURCHASE_INVOICE_CREATE) ? (

            <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${base}/purchase-invoice/new`)}>

              New Invoice

            </InvButton>

          ) : null

        }

      />

    </Box>

  );

}

